import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { gql } from 'apollo-angular';
import {
  BehaviorSubject, map, Observable, Subject
} from 'rxjs';
import { AgreementJob } from 'src/app/shared/graphql/agreements/update-agreement-query.graphql';
import { RecommendedWorkersQueryService } from 'src/app/shared/graphql/jobs/recommended-worker-query.graphql.autogenerated';
import { JobCreateResponseAnswer } from 'src/app/shared/models/gql/job-create-response-answer.model';
import { JobLastResponseAnswer } from 'src/app/shared/models/gql/job-last-response-answer.model';
import { RateChecker } from 'src/app/shared/models/gql/rate-checker.model';
import { ReplyJobModal } from 'src/app/shared/models/gql/replyJobModal.model';
import { JobServiceListItem } from 'src/app/shared/models/job-service-list-item.model';
import { NetRate } from 'src/app/shared/models/price-limit-alert-data.model';
import { Recommendations } from 'src/app/shared/types/recommended-workers.model';
import { Timeslot } from 'src/app/shared/types/timeslot';
import { pageInfoFragment } from '../gql-fragments/gql-fragments';
import { Conversation } from '../models/conversation.model';
import { ConvertJobToPublicRequest } from '../models/gql/convert-job-to-public-request.model';
import { JobCreateUpdateRequest } from '../models/gql/job-create-update-request.model';
import { JobDetail } from '../models/gql/job-detail.model';
import { JobSearchFilter } from '../models/gql/job-search-filter.model';
import { PaginatedQueryPage } from '../models/gql/special/paginated-query-page.model';
import { JobMatch } from '../models/job-match.model';
import { Job } from '../models/job.model';
import { ListItem } from '../models/list-item';
import { ApiInterfaceService } from './api-interface.service';

// Posted job action mutation input type
// action - job to fulfil, cancel, archive
// reason - reason message for job fulfil or cancel
// agreementId - agreement id
// message - message to be send for the job fulfil
type PostedJobActionInput = { action: 'cancel' | 'fulfil' | 'archive'; reason?: string; agreementId?: string | number; message?: string };

// Client posted job query filters type
export type ClientPostedJobs = 'current' | 'archived' | 'pending' | 'all' | 'flex' | 'flex_current' | 'flex_archived' | 'flex_pending' | 'private' | 'private_current' | 'private_archived' | 'private_pending';

interface JobResponseMessage {
  message: string;
  to: string;
  title: string;
  is_referred_by_recommended: boolean;
  timeslots: Timeslot[];
}

/**
 * Provides CRUD interface for jobs. To get the current jobs
 * in a component, call list() and refresh() when necessary.
 */
@Injectable({
  providedIn: 'root',
})
export class JobsService {
  /** Class members */

  // This variable is used to pass private jobs object
  private privateJobsObjectEvent: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  public conversationToShareJob: Partial<Conversation>;

  // when fired, this lets subscribers know that the job list needs refreshing.
  public refreshList: Subject<void> = new Subject();

  // TODO: ES-15056 create an interface for describing a particular day of the week same in web.
  // Expected weekdays array
  public weekDaysOptions = [
    {
      shortDay: 'Mon', day: 'Monday',
    },
    {
      shortDay: 'Tue', day: 'Tuesday',
    },
    {
      shortDay: 'Wed', day: 'Wednesday',
    },
    {
      shortDay: 'Thu', day: 'Thursday',
    },
    {
      shortDay: 'Fri', day: 'Friday',
    },
    {
      shortDay: 'Sat', day: 'Saturday',
    },
    {
      shortDay: 'Sun', day: 'Sunday',
    },
  ];

  // The private to public job conversion spans multiple steps, we use this behaviour subject to pass the private job object between the steps as it is converted to a private job
  postToLocalWorkersObjectEvent: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  // Can Show JobShareSuccess Screen
  canShowJobShareSuccessPage$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // To Show JobShareSuccess Screen it shouldn't fom shortlist page
  private firstTimeConversationStarterFlag = false;

  constructor(
    private api: ApiInterfaceService,
    public alertCtrl: AlertController,
    private recommendedWorkersQueryService: RecommendedWorkersQueryService,
  ) { }

  /**
   * Update private jobs object using behavior subject.
   *
   * @param res - Private jobs object data.
   */
  updatePrivateJobsObject(res) {
    this.privateJobsObjectEvent.next(res);
  }

  /**
   * Update Private To Public Jobs object using behavior subject.
   *
   * @param res - Private jobs object data.
   */
  updatepostToLocalWorkersObject(res) {
    this.postToLocalWorkersObjectEvent.next(res);
  }

  /**
   * Fetch private jobs object .
   *
   */
  getPrivateJobsObject() {
    return this.privateJobsObjectEvent.asObservable();
  }

  /**
   * Get Observable to show job shared success page.
   *
   */
  get showJobShareSuccessPage$(): Observable<boolean> {
    return this.canShowJobShareSuccessPage$.asObservable();
  }

  /**
   * Set Observable to show job shared success page.
   * This will be set true when its from worker search page.
   * When it's from shortlist to profile page then it's false.
   * When it's from inbox attaching the job description then it's false.
   * When it's from recommended job then it's false.
   */
  set canShowJobShareSuccessPageSource(value: boolean) {
    this.canShowJobShareSuccessPage$.next(value);
  }

  /**
   * Get only SP who is starting the conversation first time.
   * only taken to job shared success page.
   *
   */
  get firstTimeConversationStarter() {
    return this.firstTimeConversationStarterFlag;
  }

  /**
   * Set only SP who is starting the conversation first time.
   */
  set firstTimeConversationStarter(value: boolean) {
    this.firstTimeConversationStarterFlag = value;
  }

  /**
   * Fire an event to let other pages know that they need to refresh their job list.
   *
   */
  refreshJobList() {
    this.refreshList.next();
  }

  /**
   * Sends a request for get job list for client.
   *
   * @param filters - Job filter to get job list.
   * @param pageInfo - Page info that will contain first, last, after and before.
   * @param pageInfo.first - First how many data we want from graphql query.
   * @param pageInfo.after - After use to get the next page data for the specified cursor value.
   */
  async getClientPostedJobs(filters: ClientPostedJobs[], pageInfo: { first: number; after?: string }): Promise<PaginatedQueryPage<(JobDetail)>> {
    try {
      const res = await this.api.gqlQuery(gql`
      query clientPostedJobs(
        $filters: [PostedJobClientFilterOptions!]!,
        $after: String,
        $first: Int
        ) {
        clientPostedJobs(
          filters: $filters,
          first: $first,
          after: $after,
          ){
          nodes {
            id
            approved
            title
            frequency
            supportSessionsPerWeek
            estimatedHoursPerWeek
            estimatedHoursPerPeriod
            supportSessionsPerPeriod
            sessionFrequency
            hoursDescription
            clientFields {
              repliedCount
            }
            status {
              clientState
              clientStatusDescription,
              clientStatus
            }
            timestamps {
              createdAt
              startTime
              endTime
            }
            serviceCategories {
              name
            }
            days {
              summary
            }
            jobKind {
              kind
            }
            participant {
              firstName
              age
            }
            location {
              address
            }
          }
        totalCount
        pageInfo{
          ...PageInfo
        }
        }
      }
      ${pageInfoFragment} `, {
        filters,
        ...pageInfo,
      });

      return res['clientPostedJobs'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a request for get job list for workers.
   *
   * @param searchFiltersParams - Job filter to get job list.
   * @param searchJobFiltersParams
   */
  async getWorkersPostedJobs(searchJobFiltersParams: JobSearchFilter): Promise<PaginatedQueryPage<(JobDetail)>> {
    try {
      const res = await this.api.gqlQuery(gql`
       query search(
        $task: WorkerJobSearchTaskOptions!,
        $searchFilter: JobSearch,
        $first: Int!,
        $after: String!
        ){
          searchJobs(
            task:$task,
            searchFilter: $searchFilter,
            first:$first,
            after: $after
             ){
            nodes {
              id
              uuid
              title
              hoursDescription
              requestersCount
              status {
                clientStatusDescription,
                clientStatus
                workerStatus
              }
              timestamps {
                createdAt
                startTime
                endTime
                closingTime
              }
              days {
                summary
              }
              badges {
                days
                details
                hours
                summary
              }
              workerFields {
                  isSaved
                  repliedCount
                  allowActions {
                    archive
                    decline
                    reply
                    report
                  }
                  match {
                    repliedAt
                  }
                  matchExplanation {
                    name
                    percentage
                  }
                  travelTime
              }
              jobKind {
                kind
                isFlex
              }
              participant {
                firstName
                age
              }
              location {
                address
              }
              frequency
              scheduling
              supportSessionsPerWeek
              estimatedHoursPerWeek
              timingDescription
              jobSessions {
                id
                weekDay
                period {
                  startTime
                  endTime
                }
              }
            }
          totalCount
          pageInfo{
            ...PageInfo
          }
          }
        }
        ${pageInfoFragment}
        `, {
        ...searchJobFiltersParams,
      });
      return res['searchJobs'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a request to fetch job details.
   *
   * @param id - Id for get data for particular job.
   */
  async getPostedJobDetailsById(id: string | number): Promise<JobDetail> {
    try {
      const res = await this.api.gqlQuery(gql`
        query postedJob($id: ID!){
          postedJob(id: $id) {
                id
                title
                uuid
                frequency
                sessionFrequency
                hours
                hoursDescription
                consumerDescription
                carerDescription
                jobDescription
                approved
                badges {
                  details
                  hours
                  days
                  summary
                }
                scheduling
                jobKind {
                  kind
                  isFlex
                }
                supportDetails {
                  name
                  details
                }
                status {
                  clientState
                  clientStatusDescription
                  canConvertToPublic
                  clientStatus
                  workerStatus
                }
                timestamps {
                  createdAt
                  endTime
                  closingTime
                  startTime
                  expiryDate
                }
                participant {
                  id
                  firstName
                  age
                  gender
                  fullName
                  accountManagerName
                  displayFirstName
                  aboutSocialNeeds
                }
                preferredWorker {
                  gender
                }
                location {
                  address
                  postCodeGeoId
                }
                serviceCategories {
                  name
                  code
                }
                days {
                  details {
                    day
                    session
                  }
                  summary
                  times
                }
                client {
                  id
                  truncatedName
                  displayName
                  uuid
                }
                workerFields {
                  repliedCount
                  canCreateAgreement
                  isQualified
                  userHasBlocked,
                  userIsBlocked,
                  canSeeClientInfo,
                  allowActions {
                    archive
                    decline
                    reply
                    report
                  }
                  conversation {
                    id
                    sendbirdChannelDetail {
                      channelUrl
                    }
                  }
                  agreement{
                    id
                    isRequested
                    status
                  }
                  match {
                    amount
                    distance
                    comment
                    postedJob {
                      id
                    }
                    user {
                      id
                      truncatedName
                      displayName
                      avatar {
                        small
                      }
                      profile {
                        ... on WorkerProfile {
                          myType
                        }
                      }
                    }
                  }
                  userHasSentMessage
                  isSaved
                }
                clientFields {
                  repliedCount
                  acceptedMatch {
                    agreement {
                      id
                    }
                    amount
                    distance
                    comment
                    postedJob {
                      id
                    }
                    user {
                      id
                      truncatedName
                      displayName
                      avatar {
                        small

                      }
                      profile {
                        ... on WorkerProfile {
                          combinedFeedback
                        }
                      }
                    }

                  }
                }
                supportSessionsPerPeriod
                estimatedHoursPerPeriod
                supportSessionsPerWeek
                estimatedHoursPerWeek
                timingDescription
                jobSessions {
                  id
                  weekDay
                  period {
                    startTime
                    endTime
                  }
                }
              }
            } `, { id });
      return res['postedJob'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a job response from a worker for a jobId with message.
   *
   * @param jobId - the job id
   * @param {JobResponseMessage} message - the message as a
   */
  sendJobResponse(
    jobId: string | number,
    message: JobResponseMessage
  ): Observable<ListItem<Job>> {
    return this.api.post$<ListItem<Job>>(`/api/v1/workers/posted_jobs/${jobId}/posted_job_replies.json`, { message }, true, null);
  }

  /**
   * Sends a mutation to update posted job status.
   *
   * @param job
   * @param postedJobActionInput - Contain posted job id, action type, reason, agreement id, message.
   */
  async updatePostedJobStatus(job: Job | AgreementJob | JobDetail, postedJobActionInput: PostedJobActionInput): Promise<any> {
    try {
      const res = await this.api.gqlMutation(gql`
      mutation updatePostedJobStatus($postedJobAction:UpdatePostedJobStatusInput!) {
        updatePostedJobStatus(input:$postedJobAction)
        {
          clientMutationId
        }
      }`, {
        postedJobAction: {
          postedJobId: job?.id,
          ...postedJobActionInput,
        },
      });
      return res['updatePostedJobStatus'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Return carer matches for job. A "job match" may mean different things depending on the context.
   * For private jobs for example, it is carers that the job has been shared with. For regular jobs, it is
   * applicants for a job. Etc.
   *
   * @param jobId - job id for which you want matches.
   */
  async getJobMatches(jobId: string | number): Promise<JobMatch[]> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query recommendedWorkers($jobId: ID!) {
            postedJob(id: $jobId) {
              clientFields {
                jobShares {
                  id
                  permalink
                  truncatedName
                  avatar {
                    small
                  }
                }
              }
            }
          }
        `,
        { jobId }
      );
      return res['postedJob']['clientFields']['jobShares'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a request to fetch reason options that can be used for cancel/mark as filled a job.
   *
   * @param filterType - Posted Job Reasons either fulfilled or cancelled.
   */
  async getFulfillCancelReasons(filterType: 'fulfilled' | 'cancelled'): Promise<any> {
    try {
      const fulfilCancelJobReasonsData = await this.api.gqlQuery(gql`
        query {
          __type(name: "PostedJobFulfilCancelReasonOptions") {
            name
            enumValues {
              name
              description
            }
          }
        }
      `);
      return fulfilCancelJobReasonsData['__type']['enumValues'].filter((fullfilCancellationFilterOption) => fullfilCancellationFilterOption.name.split('_')[0] === filterType);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a mutation for converting a private job to public job.
   *
   * @param privateJobObject - private job object.
   */
  async convertPrivateJobToPublic(privateJobObject: ConvertJobToPublicRequest): Promise<any> {
    try {
      const res = await this.api.gqlMutation(gql`
      mutation convertJobToPublic($convertJobToPublicInput:ConvertJobToPublicInput!) {
        convertJobToPublic(input:$convertJobToPublicInput)
        {
          postedJob {
            id
          }
        }
      }`, { convertJobToPublicInput: privateJobObject });
      return res['convertJobToPublic']['postedJob'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Creating and updating regular or private job.
   *
   * @param postedJobCreateUpdateInput - job create or update object.
   */
  async createUpdateRegularOrPrivateJob(postedJobCreateUpdateInput: JobCreateUpdateRequest): Promise<any> {
    try {
      const postedJobData = await this.api.gqlMutation(gql`
        mutation postedJobCreateUpdate($postedJobCreateUpdateInput:PostedJobCreateUpdateInput!) {
          postedJobCreateUpdate(input:$postedJobCreateUpdateInput) {
            postedJob {
              id
            }
          }
        }
        `, { postedJobCreateUpdateInput });
      return postedJobData['postedJobCreateUpdate']['postedJob'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Generate the job title to be displayed when a carer is creating an agreement for their client.
   * This is used when using the new streamline agreement creation view.
   *
   * @param job - the shared or replied job from the client.
   * @returns the title of the job to be displayed on the job selection section in agreement creation page.
   */
  generateJobTitle(job: AgreementJob): string {
    if (!job) {
      return 'Support';
    }

    if (job.title?.length) {
      return job.title;
    }

    if (job.jobKind?.kind === 'private') {
      let title = '';

      const services = job.serviceCategories?.length !== 0 ? (job.serviceCategories as any[]).map((item) => item['name']) : [];
      if (services.length === 0) {
        title = 'Support';
      } else if (services.length <= 2) {
        title = services.toString();
      } else if (services.length >= 3) {
        title = `${services.splice(0, 2).join(', ')} and other services`;
      }

      return title;
    }

    return 'Support';
  }

  /**
   * generate private job title based on selected services and selected participant's age
   * if called for other kind of jobs it will return original title only
   * this is needed to cover cases e.g. job was created when the participant was a child, but job is viewed after participant turns into an adult
   * or if after creating a job client changes the birth date of the participant in the profile the title can wrongly say a child instead of an adult
   * the reason this logic is here in frontend instead of backend is because of performance issues it will create for job list APIs
   *
   * @param job
   * @param isWithGraphQl
   */
  generateTitle(job: Job | AgreementJob | JobDetail, isWithGraphQl = false) {
    if (isWithGraphQl) {
      job = job as AgreementJob;
      if (job?.jobKind?.kind === 'private') {
        let title = '';
        const services = job?.serviceCategories?.length !== 0 ? (job?.serviceCategories as any[]).map((item) => item['name']) : [];

        if (typeof job?.title !== 'undefined' && (job?.title !== '' || job?.title !== null)) { // if job title exists
          return job.title;
        }

        if (services.length === 0) { // Support
          title = 'Support';
        } else if (services.length <= 2) { // Provide transport, Companionship and social support
          title = services.toString();
        } else if (services.length >= 3) { // Provide transport, Companionship and social support and other services
          title = `${services.splice(0, 2).join(', ')} and other services`;
        }
        // if participant age is not null, greater than 1 and less than 150 then only use it in the job title
        // this is because our data analysis shows that there are more than 25k participants in database right now with age = null, 0 or 190
        // another underlying reason is that participant birth date on client profile is an optional field as of now
        // which will become mandatory for new clients onboarding but we still need to cover for older clients on platform
        const participantAge = Number(job.participant?.age);
        if (participantAge != null && participantAge > 1 && participantAge < 150) { // for an adult OR for a child
          title += ` for ${participantAge >= 18 ? 'an adult' : 'a child'}`;
        }
        return title;
      }
    } else {
      job = job as Job;
      if (job.kind === 'private') {
        let title = '';
        const services = job.services ? job.services.split(',') : [];

        if (typeof job?.title !== 'undefined' && (job?.title !== '' || job?.title !== null)) { // if job title exists
          return job.title;
        }

        if (services.length === 0) { // Support
          title = 'Support';
        } else if (services.length <= 2) { // Provide transport, Companionship and social support
          title = job.services;
        } else if (services.length >= 3) { // Provide transport, Companionship and social support and other services
          title = `${services.splice(0, 2).join(', ')} and other services`;
        }
        // if participant age is not null, greater than 1 and less than 150 then only use it in the job title
        // this is because our data analysis shows that there are more than 25k participants in database right now with age = null, 0 or 190
        // another underlying reason is that participant birth date on client profile is an optional field as of now
        // which will become mandatory for new clients onboarding but we still need to cover for older clients on platform
        const participantAge = job.participant?.age;
        if (participantAge != null && participantAge > 1 && participantAge < 150) { // for an adult OR for a child
          title += ` for ${participantAge >= 18 ? 'an adult' : 'a child'}`;
        }
        return title;
      }
    }
    return job.title;
  }

  /**
   * Sends a POST request to set flex availability.
   *
   * @param available - flex available or not.
   */
  async setUserAvailability(available: boolean): Promise<any> {
    try {
      const res = await (<any>(await this.api.post(`/workers/flex_availabilities/enabled.json?value=${available}`, null, true)).toPromise());
      return res;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a GET request to fetch new incidents.
   *
   * @param id - Id for get data for particular job.
   */
  async getNewIncident(id: string | number): Promise<any> {
    try {
      const res = await (<any>(await this.api.get(`/api/v1/workers/posted_jobs/${id}/incidents/new.json`, true)).toPromise());
      return res.data.attributes;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a POST request for report a job.
   *
   * @param id - Id of the particular job.
   * @param reason - reason to report a job
   */
  async reportJob(id: string | number, reason: string): Promise<ListItem<Job>> {
    try {
      const res = await (<any>(await this.api.post(`/api/v1/workers/posted_jobs/${id}/incidents.json`, { incident: { reason } }, true)).toPromise());
      return res.data;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Fetch a list of recommended workers for a job.
   *
   * @param jobId - The ID of the job to fetch workers for.
   * @returns A list of recommended workers along with their recommendation explanations.
   */
  getWorkerRecommendations(jobId: string): Observable<Recommendations> {
    return this.recommendedWorkersQueryService
      .fetch({ jobId })
      .pipe(map((response) => response.data?.recommendedWorkers?.recommendations ?? []));
  }

  /**
   * Success alert handler
   *
   * @param action - action type
   */
  successHandler(action: string) {
    // create job success handler
    this.alertCtrl.create({
      header: 'Success',
      message: `Job was ${action} successfully.`,
      buttons: [
        {
          text: 'Ok',
        },
      ],
    }).then((alert) => {
      alert.present();
    });

    // create job error handler
  }

  /**
   * Error alert handler
   *
   * @param action - action type
   */
  errorHandler(action: string) {
    // create job error handler
    this.alertCtrl.create({
      buttons: [{ text: 'Ok' }],
      header: 'Error',
      message: `Job could not be ${action}, please retry later.`,
    }).then((alert) => {
      alert.present();
    });
  }

  /**
   * get job session weekdays for which time slots are available.
   *
   */
  getJobSessionTimeSlotsWeekDays(jobSessionTimeSlots: {
    [key: string]: JobDetail['jobSessions'];
  }) {
    const hoursAvailable = this?.weekDaysOptions.every((item) => jobSessionTimeSlots[item?.day]?.length > 0);
    if (hoursAvailable) {
      return 'Monday-Sunday';
    }
    return Object.keys(jobSessionTimeSlots).map((day) => day).join(', ');
  }

  /**
   * Sends a request to get the service list.
   *
   */
  async getServicesList(): Promise<JobServiceListItem[]> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query {
            clientPostedJobsServiceCategories(category: null) {
              id
              title
              subTitle
              isImportant
              details
            }
          }
        `,
      );
      return res['clientPostedJobsServiceCategories'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get calculated rate from backend.
   *
   * @param {RateChecker} rateObj - RateChecker object for getting net rate.
   * @returns {NetRate} - NetRate object containing net rate and price limit informaiton.
   */
  async getLMJRates(rateObj: RateChecker): Promise<NetRate> {
    try {
      const res = await this.api.gqlQuery(gql`
      query calculatedRate($baseRate: Float!, $consumerId: ID, $timeAndPlace: TimeAndPlace){
        calculatedRate(baseRate: $baseRate, consumerId: $consumerId, timeAndPlace: $timeAndPlace) {
          rateWithFee
          exceedCapRate
          priceLimitAlertType
          capRate
          priceLimitAlertToDType
        }
      }`, rateObj);
      return res['calculatedRate'];
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }

  /**
   * Sends a request to get the reply job modal field.
   *
   * @param id - job id
   */
  async getReplyJobModalField(id: string | number): Promise<ReplyJobModal> {
    try {
      const res = await this.api.gqlQuery(gql`
        query postedJob($id: ID!){
          postedJob(id: $id) {
              workerFields {
                jobRepliedModalFields{
                  title
                  listItems
                  iconName
                  ctaActions{
                    ctaText
                    ctaLink
                    ctaType
                  }
                }
              }
            }
          }
        `, { id });
      return res['postedJob']['workerFields']['jobRepliedModalFields'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get the last job application response answers.
   *
   */
  getLastJobResponseAnswers$(): Observable<JobLastResponseAnswer[]> {
    type LastJobResponseAnswers = { lastJobResponseAnswers: JobLastResponseAnswer[] };
    return this.api
      .gqlQuery$<LastJobResponseAnswers>(
        gql`
          {
            lastJobResponseAnswers(answerTypes: [introduction]) {
              answerType
              content
            }
          }
        `,
      )
      .pipe(map((result) => result.lastJobResponseAnswers));
  }

  /**
   * Send the GraphQL mutation request to save the latest job application response answers.
   *
   * @param lastJobResponseAnswerAttributes - an array of job application reponse answers.
   */
  createJobResponseAnswers$(lastJobResponseAnswerAttributes: JobCreateResponseAnswer[]): Observable<JobLastResponseAnswer[]> {
    type CreateJobResponseAnswer = {
      createJobResponseAnswer: {
        jobResponseAnswers: JobLastResponseAnswer[]
      }
    };
    return this.api
      .gqlMutation$<CreateJobResponseAnswer>(gql`
          mutation ($CreateJobResponseAnswerInput: CreateJobResponseAnswerInput!) {
            createJobResponseAnswer(input: $CreateJobResponseAnswerInput) {
              jobResponseAnswers {
                content
                answerType
              }
            }
          }
        `, { CreateJobResponseAnswerInput: { lastJobResponseAnswerAttributes } })
      .pipe(map((result) => result.createJobResponseAnswer.jobResponseAnswers));
  }
}
